import React, { useRef } from 'react';
import { Station, ProjectConfig } from '../../types';
import { useStationForm } from '../../hooks/useStationForm';
import { LatLongTooltip } from '../common/LatLongTooltip';
import { useModalClose } from '../../hooks/useModalClose';

interface Props {
  station: Station;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (updates: Partial<Station>) => void;
  onDelete: () => void;
  config: ProjectConfig;
  project: { identifier: string; config: ProjectConfig };
}

const LoadingSpinner = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <style>
      {`.spinner_qM83{animation:spinner_8HQG 1.05s infinite}.spinner_oXPr{animation-delay:.1s}.spinner_ZTLf{animation-delay:.2s}@keyframes spinner_8HQG{0%,57.14%{animation-timing-function:cubic-bezier(0.33,.66,.66,1);transform:translate(0)}28.57%{animation-timing-function:cubic-bezier(0.33,0,.66,.33);transform:translateY(-6px)}100%{transform:translate(0)}}`}
    </style>
    <circle className="spinner_qM83" cx="4" cy="12" r="3" fill="#666"/>
    <circle className="spinner_qM83 spinner_oXPr" cx="12" cy="12" r="3" fill="#666"/>
    <circle className="spinner_qM83 spinner_ZTLf" cx="20" cy="12" r="3" fill="#666"/>
  </svg>
);

export const NodeContextMenu = ({ station, position, onClose, onUpdate, onDelete, config, project }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    name,
    setName,
    population,
    latitude,
    longitude,
    isLoading,
    populationSource,
    latitudeError,
    longitudeError,
    handlePopulationChange,
    handleCoordinateInput,
    handleSaveAndClose,
    handlePopulationSourceChange,
    savePopulation
  } = useStationForm({
    station,
    onUpdate,
    onClose,
    config
  });

  // Focus input on mount
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [station]);

  // Force re-render when circle population changes
  React.useEffect(() => {
    // This effect will trigger a re-render when circle_population changes
  }, [station.circle_population]);

  const handleEnter = () => {
    if (name !== station.name) {
      console.log('NodeContextMenu:', {
        event: 'name_updated',
        stationId: station.id,
        oldName: station.name,
        newName: name
      });
      onUpdate({ name });
    }
    savePopulation();
    handleSaveAndClose(true);
  };

  const modalRef = useModalClose({
    onClose: () => handleSaveAndClose(true),
    project,
    view: 'node'
  });

  return (
    <div 
      ref={modalRef}
      style={{
        position: 'fixed',
        left: position.x + 20,
        top: position.y - 50,
        background: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '12px',
        zIndex: 1000,
        overflow: 'hidden'
      }}
    >
      <div style={{
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        gap: '8px'
      }}>
        <button
          onClick={onDelete}
          disabled={isLoading}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            color: isLoading ? '#999' : '#666',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => !isLoading && (e.currentTarget.style.color = '#dc2626')}
          onMouseLeave={(e) => !isLoading && (e.currentTarget.style.color = '#666')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
          </svg>
        </button>
        <button
          onClick={() => handleSaveAndClose(true)}
          disabled={isLoading}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            color: isLoading ? '#999' : '#666',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isLoading ? <LoadingSpinner /> : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          )}
        </button>
      </div>
      <div style={{ marginBottom: '8px', marginTop: '16px' }}>City</div>
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleEnter();
          }
        }}
        onBlur={() => {
          if (name !== station.name) {
            onUpdate({ name });
          }
        }}
        data-1p-ignore
        style={{
          width: '100%',
          padding: '4px 8px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          marginBottom: '24px'
        }}
      />
      <div style={{
        height: '1px',
        background: '#e0e0e0',
        margin: '0 -12px 0 -12px'
      }} />
      <div style={{
        background: '#f8f8f8',
        margin: '0 -12px -12px -12px',
        padding: '24px 12px'
      }}>
        <div style={{ marginBottom: '8px' }}>Population Source</div>
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '12px',
          padding: '4px 0'
        }}>
          <button
            onClick={() => handlePopulationSourceChange('manual')}
            style={{
              padding: '0 12px',
              height: '42px',
              borderRadius: '6px',
              fontSize: '14px',
              width: '120px',
              backgroundColor: populationSource === 'manual' ? '#dbeafe' : 'white',
              color: populationSource === 'manual' ? '#1d4ed8' : '#374151',
              border: populationSource === 'manual' ? 'none' : '1px solid #e5e7eb',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (populationSource !== 'manual') {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (populationSource !== 'manual') {
                e.currentTarget.style.backgroundColor = 'white';
              }
            }}
          >
            Manual
          </button>
          <button
            onClick={() => handlePopulationSourceChange('automatic')}
            style={{
              padding: '0 12px',
              height: '42px',
              borderRadius: '6px',
              fontSize: '14px',
              width: '120px',
              backgroundColor: populationSource === 'automatic' ? '#dbeafe' : 'white',
              color: populationSource === 'automatic' ? '#1d4ed8' : '#374151',
              border: populationSource === 'automatic' ? 'none' : '1px solid #e5e7eb',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (populationSource !== 'automatic') {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (populationSource !== 'automatic') {
                e.currentTarget.style.backgroundColor = 'white';
              }
            }}
          >
            Automatic
          </button>
        </div>
        {populationSource === 'manual' ? (
          <>
            <div style={{ marginBottom: '8px' }}>Population</div>
            <input
              type="text"
              value={population}
              onChange={(e) => handlePopulationChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  savePopulation();
                  handleSaveAndClose(true);
                }
              }}
              onBlur={() => {
                savePopulation();
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginBottom: '12px'
              }}
            />
            {station.circle_population !== null && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                marginBottom: '0'
              }}>
                Population within {station.circle_population_radius}km radius: {new Intl.NumberFormat('en-US').format(station.circle_population || 0)}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ marginBottom: '8px' }}>
              <div className="flex items-center gap-1">
                <span>Latitude</span>
                <LatLongTooltip
                  content={
                    <>
                      Enter a value between -90 and 90. Accepted formats:
                      <ul className="mt-2 list-disc list-inside">
                        <li>Single value (e.g. <code>48.8588255</code>)</li>
                        <li>Comma-separated pair (e.g. <code>48.8588255,2.2646344</code>)</li>
                        <li>Comma-separated pair with additional data (e.g. <code>48.8588255,2.2646344,24664m</code>)</li>
                      </ul>
                    </>
                  }
                />
              </div>
            </div>
            <input
              type="text"
              value={latitude === null ? '' : latitude}
              onChange={(e) => handleCoordinateInput(e.target.value, 'latitude')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAndClose(true);
                }
              }}
              onBlur={() => {
                if (latitude !== station.latitude || longitude !== station.longitude) {
                  handleSaveAndClose(false);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: `1px solid ${latitudeError ? '#dc2626' : '#e0e0e0'}`,
                borderRadius: '4px',
                marginBottom: '12px',
                backgroundColor: latitudeError ? '#fef2f2' : 'white'
              }}
            />
            <div style={{ marginBottom: '8px' }}>
              <div className="flex items-center gap-1">
                <span>Longitude</span>
                <LatLongTooltip
                  content={
                    <>
                      Enter a value between -180 and 180. Accepted formats:
                      <ul className="mt-2 list-disc list-inside">
                        <li>Single value (e.g. <code>2.2646344</code>)</li>
                        <li>Comma-separated pair (e.g. <code>48.8588255,2.2646344</code>)</li>
                        <li>Comma-separated pair with additional data (e.g. <code>48.8588255,2.2646344,24664m</code>)</li>
                      </ul>
                    </>
                  }
                />
              </div>
            </div>
            <input
              type="text"
              value={longitude === null ? '' : longitude}
              onChange={(e) => handleCoordinateInput(e.target.value, 'longitude')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAndClose(true);
                }
              }}
              onBlur={() => {
                if (latitude !== station.latitude || longitude !== station.longitude) {
                  handleSaveAndClose(false);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: `1px solid ${longitudeError ? '#dc2626' : '#e0e0e0'}`,
                borderRadius: '4px',
                marginBottom: '12px',
                backgroundColor: longitudeError ? '#fef2f2' : 'white'
              }}
            />
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              marginBottom: '0'
            }}>
              {station.circle_population !== null && (
                <>Population within {station.circle_population_radius}km radius: {new Intl.NumberFormat('en-US').format(station.circle_population || 0)}</>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 